/**
 * Engaged time: how long a signed-in user has Crimson in the foreground.
 *
 * Time counts only while the tab is visible, and stops 1 minute after the
 * last click, keypress, or scroll so a tab left open on another monitor does
 * not keep accruing. Samples are emitted at least once a minute and whenever
 * the page changes, so each slice stays attached to the page it was spent on.
 *
 * `page` is the route template (`/matter/$slug/chronology`, or
 * `/admin?tab=analytics` when a tab is the page). `pathname` is the concrete
 * path, including the matter. Query in Application Insights:
 *
 *   customEvents
 *   | where name == "EngagedTime"
 *   | extend seconds = todouble(customMeasurements.seconds)
 *   | extend page = tostring(customDimensions.page)
 *   | extend pathname = tostring(customDimensions.pathname)
 *   | summarize minutes = sum(seconds) / 60.0 by user_AuthenticatedId, page
 */

export const ENGAGED_TIME_EVENT = 'EngagedTime';
export const ENGAGED_FLUSH_INTERVAL_MS = 60_000;
export const ENGAGED_IDLE_TIMEOUT_MS = 60_000;

export type EngagedPage = {
  page: string;
  pathname: string;
};

export type EngagedTimeSample = EngagedPage & {
  seconds: number;
};

const TAB_PARAM = /^[A-Za-z0-9-]{1,40}$/;

export function pageFromRoute(fullPath: string | undefined, pathname: string, search: unknown): EngagedPage {
  const route = fullPath || pathname;
  const tab = tabFromSearch(search);
  return {
    page: tab ? `${route}?tab=${tab}` : route,
    pathname,
  };
}

function tabFromSearch(search: unknown): string | null {
  if (!search || typeof search !== 'object' || !('tab' in search)) return null;
  const tab = (search as { tab?: unknown }).tab;
  if (typeof tab !== 'string' || !TAB_PARAM.test(tab)) return null;
  return tab;
}

type TrackerOptions = {
  now: () => number;
  onSample: (sample: EngagedTimeSample) => void;
};

export type EngagedTimeTracker = {
  start: (page: EngagedPage, visible: boolean) => void;
  stop: () => void;
  setPage: (page: EngagedPage) => void;
  noteActivity: () => void;
  setVisible: (visible: boolean) => void;
  checkpoint: () => void;
};

export function createEngagedTimeTracker({ now, onSample }: TrackerOptions): EngagedTimeTracker {
  let running = false;
  let visible = false;
  let segmentStart: number | null = null;
  let accumulatedMs = 0;
  let lastActivityAt = 0;
  let page: EngagedPage = { page: '', pathname: '' };

  const accrue = (at: number) => {
    if (segmentStart == null) return;
    const activeUntil = lastActivityAt + ENGAGED_IDLE_TIMEOUT_MS;
    const end = Math.min(at, activeUntil);
    if (end > segmentStart) {
      accumulatedMs += end - segmentStart;
    }
    segmentStart = visible && at < activeUntil ? at : null;
  };

  const resume = (at: number) => {
    if (visible && at < lastActivityAt + ENGAGED_IDLE_TIMEOUT_MS) {
      segmentStart = at;
    }
  };

  // `dropRemainder` clears a sub-second tail so it is not attributed to the next page.
  const emit = (dropRemainder: boolean) => {
    accrue(now());
    const seconds = Math.floor(accumulatedMs / 1000);
    if (dropRemainder) {
      accumulatedMs = 0;
    } else if (seconds >= 1) {
      accumulatedMs -= seconds * 1000;
    }
    if (seconds >= 1 && page.page) {
      onSample({ page: page.page, pathname: page.pathname, seconds });
    }
  };

  return {
    start(initialPage, isVisible) {
      if (running) return;
      running = true;
      page = initialPage;
      visible = isVisible;
      lastActivityAt = now();
      segmentStart = isVisible ? lastActivityAt : null;
    },

    stop() {
      if (!running) return;
      emit(false);
      running = false;
      segmentStart = null;
    },

    setPage(next) {
      if (!running) return;
      if (next.page === page.page && next.pathname === page.pathname) return;
      emit(true);
      page = next;
      resume(now());
    },

    noteActivity() {
      if (!running) return;
      const at = now();
      accrue(at);
      lastActivityAt = at;
      if (visible) segmentStart = at;
    },

    setVisible(isVisible) {
      if (!running || isVisible === visible) return;
      if (!isVisible) {
        emit(false);
        visible = false;
        segmentStart = null;
        return;
      }
      visible = true;
      lastActivityAt = now();
      segmentStart = lastActivityAt;
    },

    checkpoint() {
      if (!running) return;
      emit(false);
    },
  };
}
